/**
 * Admin Dashboard — Query helpers & types.
 * Extracted to keep the main dashboard file under 1200 lines.
 */

import { supabase } from '@/lib/supabase';

// ─── Tier Pricing ────────────────────────────────────────────────
export const TIER_PRICES: Record<string, number> = {
  free: 0,
  pro: 14.99,
  coach_pro: 60,
  coach: 60,
  org_pro: 199,
  club_starter: 199,
  club_growth: 499,
  club_performance: 700,
  club_elite: 999,
};

// ─── Test Account Filter ─────────────────────────────────────────
/**
 * Returns a Supabase query builder for the given table,
 * optionally filtering out test/demo accounts.
 * For tables with a direct `email` or `handle` column (profiles),
 * applies the filter inline. For other tables, we rely on
 * pre-filtered user ID sets.
 */
export function getFilteredProfileQuery(excludeTest: boolean) {
  let q = supabase.from('profiles').select('*');
  if (excludeTest) {
    q = q
      .not('email', 'ilike', '%test%')
      .not('email', 'ilike', '%demo%')
      .not('email', 'ilike', '%@testmail%')
      .not('email', 'ilike', '%@douevenlift.com%')
      .not('email', 'ilike', '%testopponent%')
      .not('email', 'ilike', '%review@%')
      .not('handle', 'ilike', 'test%')
      .not('handle', 'ilike', '%alexrivera%')
      .not('handle', 'ilike', '%jordankim%');
  }
  return q;
}

export function getFilteredProfileCountQuery(excludeTest: boolean, select = 'id') {
  let q = supabase.from('profiles').select(select, { count: 'exact', head: true });
  if (excludeTest) {
    q = q
      .not('email', 'ilike', '%test%')
      .not('email', 'ilike', '%demo%')
      .not('email', 'ilike', '%@testmail%')
      .not('email', 'ilike', '%@douevenlift.com%')
      .not('email', 'ilike', '%testopponent%')
      .not('email', 'ilike', '%review@%')
      .not('handle', 'ilike', 'test%')
      .not('handle', 'ilike', '%alexrivera%')
      .not('handle', 'ilike', '%jordankim%');
  }
  return q;
}

// ─── Types ───────────────────────────────────────────────────────

export interface UserRow {
  id: string;
  handle: string | null;
  display_name: string | null;
  email: string | null;
  gender: string | null;
  bodyweight: number | null;
  height: string | null;
  ethnicity: string | null;
  units: string | null;
  lifting_score: number | null;
  duel_standard_strength_kg: number | null;
  created_at: string;
  birth_year: number | null;
  subscription_tier: string | null;
  home_gym_name: string | null;
  onboarding_completed: boolean | null;
}

export interface RevenueMetrics {
  mrr: number;
  arr: number;
  arpu: number;
  tierCounts: Record<string, number>;
  payingUsers: number;
  churnCount: number;
  churnRate: number;
  ltv: number;
}

export interface GrowthMetrics {
  dau: number;
  wau: number;
  mau: number;
  newThisWeek: number;
  newThisMonth: number;
  weeklyGrowth: { label: string; count: number }[];
  totalGyms: number;
  activeGyms: number;
  newGymsThisMonth: number;
  avgMembersPerGym: number;
  waitlistCount: number;
  pendingClaims: number;
}

export interface EngagementMetrics {
  avgWorkoutsPerUserPerWeek: number;
  avgDuelsPerUserPerMonth: number;
  avgWorkoutDuration: string;
  pctLoggedWorkout: number;
  pctCreatedDuel: number;
  pctCompletedCore5: number;
  pctFollowedSomeone: number;
  peakHour: string;
}

export interface RetentionMetrics {
  d1: number;
  d7: number;
  d30: number;
  d90: number;
  cohorts: CohortRow[];
  churnByTier: Record<string, number>;
}

export interface CohortRow {
  label: string;
  size: number;
  d1: number;
  d7: number;
  d30: number;
  ageDays: number;
}

export interface FunnelStep {
  label: string;
  count: number;
  pct: number;
  dropOff: number;
}

export interface ContentMetrics {
  topExercises: { name: string; count: number }[];
  mostActiveGyms: { name: string; workouts: number }[];
  avgSetsPerWorkout: number;
  totalVolume: number;
}

export interface AppHealthMetrics {
  flagsTotal: number;
  flagsUnresolved: number;
  flagsResolvedThisWeek: number;
  activeSeasons: number;
  seasonParticipants: number;
  challengeSubmissionRate: number;
}

// ─── Helpers ─────────────────────────────────────────────────────

export function isTestAccount(u: UserRow): boolean {
  const email = (u.email || '').toLowerCase();
  const handle = (u.handle || '').toLowerCase();
  return (
    email.includes('test') ||
    email.includes('demo') ||
    email.includes('@testmail') ||
    email.includes('@douevenlift.com') ||
    email.includes('testopponent') ||
    email.includes('review@') ||
    handle.startsWith('test') ||
    handle.includes('alexrivera') ||
    handle.includes('jordankim')
  );
}

export function filterTestUsers(users: UserRow[], exclude: boolean): UserRow[] {
  if (!exclude) return users;
  return users.filter(u => !isTestAccount(u));
}

/** Distinct user IDs from workouts within a date range */
export async function getActiveUserIds(since: Date, userIdSet?: Set<string>) {
  const { data } = await supabase
    .from('workouts')
    .select('user_id')
    .gte('performed_at', since.toISOString());
  const ids = new Set((data || []).map((w: any) => w.user_id));
  if (userIdSet) {
    // intersect
    const result = new Set<string>();
    ids.forEach(id => { if (userIdSet.has(id)) result.add(id); });
    return result;
  }
  return ids;
}

export function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n * 10) / 10);
}

export function formatMoney(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function pct(num: number, den: number): number {
  return den > 0 ? Math.round((num / den) * 1000) / 10 : 0;
}
